import React from 'react';
import { Button, IconAdd16, IconEdit16, TextAreaField } from '@dhis2/ui';
import ManagerRatingInput from './ManagerRatingInput';

const ManagerCompetencyReview = ({ 
  competency, 
  index, 
  period,
  staffName,
  onSave,
  isSubmitting,
  isReadOnly,
  orgUnitId,
  hasFinalized = false // New prop to check if finalized
}) => {
  // SAFE: Ensure arrays exist with fallbacks
  const managerRatings = competency.managerRatings || [null, null];
  const managerComments = competency.managerComments || ["", ""];
  const ratings = competency.ratings || [null, null];
  const comments = competency.comments || ["", ""];
  
  const [managerRating, setManagerRating] = React.useState(managerRatings[period]);
  const [managerComment, setManagerComment] = React.useState(managerComments[period] || '');
  const [isSaving, setIsSaving] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  // Reset editing state when props change
  React.useEffect(() => {
    setManagerRating(managerRatings[period]);
    setManagerComment(managerComments[period] || '');
  }, [managerRatings, managerComments, period]);

  const handleSave = async () => {
    if (!managerRating) {
      alert('Please provide a rating before saving');
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(index, period, {
        rating: managerRating,
        comment: managerComment
      });
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setManagerRating(managerRatings[period]);
    setManagerComment(managerComments[period] || '');
    setIsEditing(false);
  };

  const hasManagerRating = managerRatings[period] !== null && managerRatings[period] !== undefined;
  
  // Determine if editable:
  // - Not finalized (hasFinalized === false)
  // - Not read-only
  // - Either no existing rating OR editing mode is active
  const isEditable = !hasFinalized && !isReadOnly;

  // If finalized, show read-only view
  if (hasFinalized) {
    return (
      <div style={{
        padding: '16px',
        border: '1px solid #9e9e9e',
        borderRadius: '8px',
        marginBottom: '16px',
        backgroundColor: '#f5f5f5',
        opacity: 0.8
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <strong style={{ fontSize: '1rem' }}>{competency.name}</strong>
          <span style={{ 
            fontSize: '0.7rem', 
            color: '#9e9e9e', 
            fontStyle: 'italic',
            backgroundColor: '#e0e0e0',
            padding: '2px 8px',
            borderRadius: '12px'
          }}>
            ✓ Finalized - Read Only
          </span>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
          <div dangerouslySetInnerHTML={{ __html: competency.definition }} />
        </p>

        {/* Staff's Self-Assessment */}
        <div style={{
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '6px',
          marginBottom: '12px'
        }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px', color: '#2c6693' }}>
            {staffName}'s Self-Assessment:
          </div>
          <div>Rating: <strong>{ratings[period] || 'Not provided'}</strong></div>
          <div style={{ marginTop: '4px' }}>
            Comment: <em>{comments[period] || 'No comment'}</em>
          </div>
        </div>

        {/* Manager's Rating - Read Only */}
        <div style={{
          padding: '12px',
          backgroundColor: '#e8f5e9',
          borderRadius: '6px',
          borderLeft: '3px solid #4caf50'
        }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px', color: '#2e7d32' }}>
            Manager's Final Rating:
          </div>
          <div>Rating: <strong>{managerRatings[period] || 'Not reviewed'}</strong></div>
          {managerComments[period] && (
            <div style={{ marginTop: '4px' }}>
              Comment: <em>"{managerComments[period]}"</em>
            </div>
          )}
          <div style={{ 
            marginTop: '8px', 
            fontSize: '0.75rem', 
            color: '#666',
            fontStyle: 'italic'
          }}>
            This assessment has been finalized by {staffName}
          </div>
        </div>
      </div>
    );
  }

  // If not finalized, show editable view
  return (
    <div style={{
      padding: '16px',
      border: `1px solid ${isEditing ? '#2196f3' : '#e0e0e0'}`,
      borderRadius: '8px',
      marginBottom: '16px',
      backgroundColor: isEditing ? '#e3f2fd' : (hasManagerRating ? '#e8f5e9' : '#fff3e0')
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <strong style={{ fontSize: '1rem' }}>{competency.name}</strong>
        {hasManagerRating && !isEditing && (
          <span style={{ 
            fontSize: '0.7rem', 
            color: '#4caf50', 
            fontStyle: 'italic',
            backgroundColor: '#e8f5e9',
            padding: '2px 8px',
            borderRadius: '12px'
          }}>
            ✓ Click to Edit
          </span>
        )}
        {!hasManagerRating && (
          <span style={{ 
            fontSize: '0.7rem', 
            color: '#ff9800', 
            fontStyle: 'italic',
            backgroundColor: '#fff3e0',
            padding: '2px 8px',
            borderRadius: '12px'
          }}>
            ⏳ Needs Review
          </span>
        )}
      </div>
      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
        <div dangerouslySetInnerHTML={{ __html: competency.definition }} />
      </p>

      {/* Staff's Self-Assessment (Read-only for manager) */}
      <div style={{
        padding: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        marginBottom: '12px'
      }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 500, marginBottom: '8px', color: '#2c6693' }}>
          {staffName}'s Self-Assessment:
        </div>
        <div>Rating: <strong>{ratings[period] || 'Not provided'}</strong></div>
        <div style={{ marginTop: '4px' }}>
          Comment: <em>{comments[period] || 'No comment'}</em>
        </div>
      </div>

      {/* Manager's Rating Section */}
      {isEditing ? (
        // Edit Mode
        <div style={{ marginTop: '12px' }}>
          <ManagerRatingInput
            value={managerRating}
            onChange={setManagerRating}
            disabled={isSubmitting}
            label="Manager's Rating"
            orgUnitId={orgUnitId}
          />
          
          <TextAreaField
            value={managerComment}
            onChange={({ value }) => setManagerComment(value)}
            placeholder="Provide feedback on staff's performance..."
            label="Manager's Comments"
            rows={3}
            disabled={isSubmitting}
            style={{ margin: '12px 0' }}
          />

          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <Button
              primary
              onClick={handleSave}
              disabled={!managerRating || isSaving || isSubmitting}
            >
              {isSaving ? 'Saving...' : 'Save Review'}
            </Button>
            <Button
              onClick={handleCancel}
              disabled={isSaving || isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        // View Mode (with Edit button if not finalized)
        <div style={{ marginTop: '12px' }}>
          {hasManagerRating ? (
            // Show saved rating with Edit button
            <div style={{
              padding: '12px',
              backgroundColor: '#e8f5e9',
              borderRadius: '6px',
              borderLeft: '3px solid #4caf50'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#2e7d32' }}>
                    Manager's Rating:
                  </div>
                  <div>Rating: <strong>{managerRatings[period]} / 5</strong></div>
                  {managerComments[period] && (
                    <div style={{ marginTop: '4px' }}>
                      Comment: <em>"{managerComments[period]}"</em>
                    </div>
                  )}
                </div>
                <button
                  style={{
                      backgroundColor: '#4e4caf',
                      color: 'white',
                      border: 'none',
                      borderRadius: '9999px',          // full pill
                      padding: '4px 14px',
                      fontSize: '0.88rem',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    }}
                  onClick={() => setIsEditing(true)}
                  disabled={isSubmitting}
                >
                 <IconEdit16 /> Edit
                </button>
              </div>
            </div>
          ) : (
            // No rating yet - show Add Rating button
            <div style={{
              padding: '12px',
              backgroundColor: '#fff3e0',
              borderRadius: '6px',
              borderLeft: '3px solid #ff9800',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.85rem', color: '#e65100', marginBottom: '8px' }}>
                ⏳ No manager rating yet
              </div>
              <button
                style={{
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '9999px',          // full pill
                      padding: '4px 14px',
                      fontSize: '0.88rem',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    }}
                onClick={() => setIsEditing(true)}
                disabled={isSubmitting}
              >
              <IconAdd16 /> Add Rating
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagerCompetencyReview;